// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { Copy, Download, Plus, Power, RefreshCw, Trash2, Wallet as WalletIcon } from "lucide-react";
import { remainingPct, uid, type WalletLedger } from "@/lib/algo/choreography";
import { walletLayoutPos, type FlowGraph, type GraphWallet } from "@/lib/algo/graph";
import { resolveDevId } from "@/lib/algo/fundNeeds";
import type { Backend } from "@/lib/algo/useBackend";
import type { RealBalance } from "@/lib/algo/api";

// Private-key import exists only in local dev builds (where you run the backend).
// import.meta.env.DEV is statically false in the public build → this whole branch
// (and the "paste private key" string) is tree-shaken out of the deployed bundle.
const CAN_IMPORT = import.meta.env.DEV;

export function WalletsButton({
  graph,
  ledgers,
  update,
  backend,
  live,
}: {
  graph: FlowGraph;
  ledgers: Map<string, WalletLedger>;
  update: (fn: (draft: FlowGraph) => void) => void;
  backend: Backend;
  live: boolean; // alpha + backend online (computed in App from the Demo/Alpha mode)
}) {
  const [open, setOpen] = useState(false);
  const [pk, setPk] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const enabledCount = graph.wallets.filter((w) => w.enabled).length;

  function addSim() {
    update((d) => {
      const i = d.wallets.length;
      d.wallets.push({ id: uid("w"), label: `Wallet ${i + 1}`, group: "core", fundingSol: 1, enabled: true, ...walletLayoutPos(i) });
    });
  }
  async function create() {
    setErr("");
    if (!live) return addSim();
    setBusy(true);
    try { await backend.createReal(1); } catch (e) { setErr(e instanceof Error ? e.message : "create failed"); } finally { setBusy(false); }
  }
  async function add() {
    setErr("");
    const key = pk.trim();
    if (!key) return;
    if (!live) { addSim(); setPk(""); return; } // sim: no real keys
    setBusy(true);
    try { await backend.importReal(key); setPk(""); } catch (e) { setErr(e instanceof Error ? e.message : "import failed"); } finally { setBusy(false); }
  }
  // ---- funding (live) ----
  const devWallet = graph.wallets.find((w) => w.id === resolveDevId(graph)) ?? graph.wallets[0];
  const mainBal = devWallet ? backend.balances.get(devWallet.id)?.sol ?? 0 : 0;
  const fundTargets = graph.wallets.filter((w) => devWallet && w.id !== devWallet.id && w.fundingSol > 0);
  const fundedCount = fundTargets.filter((w) => (backend.balances.get(w.id)?.sol ?? 0) + 1e-9 >= w.fundingSol).length;
  const shortMain = devWallet ? shortId(devWallet.publicKey || devWallet.id) : "—";

  async function fundAll() {
    if (!import.meta.env.DEV || !live || !devWallet) return; // live-only; excluded from public bundle
    setErr("");
    if (!fundTargets.length) { setErr("Set a Fund SOL amount (e.g. 0.02) on the wallets you want to fund first."); return; }
    setBusy(true);
    try {
      const transfers = fundTargets
        .map((w) => ({ toId: w.id, sol: Math.max(0, w.fundingSol - (backend.balances.get(w.id)?.sol ?? 0)) }))
        .filter((t) => t.sol > 0.0005);
      if (!transfers.length) { setErr("All wallets already at their target."); return; }
      const { apiLive } = await import("@/lib/algo/apiLive");
      const r = await apiLive.fund(devWallet.id, transfers);
      backend.refresh();
      setErr(r.results.some((x) => x.error) ? `Funded ${r.funded}/${transfers.length} — some failed.` : "");
    } catch (e) { setErr(e instanceof Error ? e.message : "fund failed"); } finally { setBusy(false); }
  }
  async function downloadWallets() {
    if (!import.meta.env.DEV) return; // live-only; excluded from public bundle
    try {
      const { apiLive } = await import("@/lib/algo/apiLive");
      const data = await apiLive.exportWallets();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "lily-wallets-backup.json"; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { setErr(e instanceof Error ? e.message : "export failed"); }
  }

  function remove(id: string) {
    if (live) void backend.removeReal(id);
    else update((d) => { d.wallets = d.wallets.filter((y) => y.id !== id); d.edges = d.edges.filter((e) => e.source !== id && e.target !== id); });
  }
  const allOn = graph.wallets.length > 0 && graph.wallets.every((w) => w.enabled);

  return (
    <div className="wallets-btn-wrap" ref={ref}>
      <button className="wallets-btn" onClick={() => setOpen((v) => !v)}>
        <WalletIcon size={14} /> Wallets <b>{graph.wallets.length}</b>
        <span className={`mode-dot ${live ? "on" : backend.online === false ? "off" : ""}`} title={live ? "Live" : "Sim"} />
      </button>

      {open && (
        <div className="wallets-pop panel">
          <div className="wpop-head">
            <span className="wpop-title">Wallets <b>{enabledCount}</b>/{graph.wallets.length} · <span className={`tag ${live ? "live" : "sim"}`}>{live ? "ALPHA" : "DEMO"}</span></span>
            <button className="link" onClick={() => update((d) => d.wallets.forEach((w) => { w.enabled = !allOn; }))}>{allOn ? "Deselect all" : "Select all"}</button>
          </div>

          <div className="wpop-add">
            {live ? (
              <>
                {/* PK import is compiled in ONLY for local dev builds — it is tree-shaken
                    out of the public bundle, so the deployed site never references keys. */}
                {CAN_IMPORT && (
                  <>
                    <input className="field" placeholder="Paste private key…" value={pk}
                      onChange={(e) => setPk(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
                    <button className="btn btn-sm" disabled={busy || !pk.trim()} onClick={add}>Add</button>
                  </>
                )}
                <button className="btn btn-sm btn-ink" disabled={busy} onClick={create}><Plus size={12} /> Create</button>
                <button className="icon-btn" title="Refresh balances" onClick={() => backend.refresh()}><RefreshCw size={13} /></button>
              </>
            ) : (
              <button className="btn btn-sm btn-ink" style={{ flex: 1 }} disabled={busy} onClick={create}><Plus size={12} /> Create simulated wallet</button>
            )}
          </div>
          {!live && <div className="wpop-note">Simulated wallets only — off-chain. No real funds, no private keys, nothing is sent to any blockchain.</div>}
          {live && devWallet && (
            <div className="wpop-fund">
              <div className="wpop-fund-main">
                <span>Main <b>{mainBal.toFixed(3)} SOL</b></span>
                <span className="wrow-addr">{shortMain}</span>
                <button className="icon-btn xs" title="Copy address" onClick={() => navigator.clipboard?.writeText(devWallet.publicKey || devWallet.id).catch(() => {})}><Copy size={10} /></button>
                <button className="icon-btn xs" title="Download wallet backup (.json)" onClick={downloadWallets}><Download size={11} /></button>
                <span className="wpop-fund-count">funded {fundedCount}/{fundTargets.length}</span>
              </div>
              <button className="btn btn-sm btn-gold" disabled={busy} title="Send SOL from Main to top every wallet up to its Fund SOL target" onClick={fundAll}>
                {busy ? "Funding…" : `Fund all wallets${fundTargets.length ? ` (${fundTargets.length})` : ""}`}
              </button>
            </div>
          )}
          {err && <div className="wpop-err">{err}</div>}

          <div className="wpop-list">
            {graph.wallets.length === 0 && <div className="wpop-empty">No wallets yet — Create one, or paste a key and Add.</div>}
            {graph.wallets.map((w) => (
              <WalletRow key={w.id} w={w} ledger={ledgers.get(w.id)} real={live ? backend.balances.get(w.id) : undefined} live={live} update={update} onRemove={() => remove(w.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WalletRow({
  w, ledger, real, live, update, onRemove,
}: {
  w: GraphWallet;
  ledger?: WalletLedger;
  real?: RealBalance;
  live: boolean;
  update: (fn: (draft: FlowGraph) => void) => void;
  onRemove: () => void;
}) {
  const rem = ledger ? remainingPct(ledger) : 0;
  const addr = w.publicKey || w.id;
  const bal = live ? real?.sol ?? 0 : ledger?.cashSol ?? w.fundingSol;
  function patch(fn: (x: GraphWallet) => void) { update((d) => { const x = d.wallets.find((y) => y.id === w.id); if (x) fn(x); }); }

  return (
    <div className={`wrow ${w.enabled ? "" : "off"}`}>
      <button className={`wrow-tog ${w.enabled ? "on" : ""}`} title={w.enabled ? "On" : "Off"} onClick={() => patch((x) => { x.enabled = !x.enabled; })}>
        <Power size={12} />
      </button>
      <div className="wrow-id">
        <input className="wrow-name" value={w.label} onChange={(e) => patch((x) => { x.label = e.target.value; })} />
        <span className="wrow-addr">{shortId(addr)}</span>
        <button className="icon-btn xs" title="Copy" onClick={() => navigator.clipboard?.writeText(addr)}><Copy size={10} /></button>
      </div>
      <input className="field wrow-fund" type="number" step="0.1" title="Fund (SOL) — auto-filled from this wallet's buys" value={w.fundingSol}
        onChange={(e) => patch((x) => { x.fundingSol = Number(e.target.value) || 0; })} />
      <span className="wrow-bal">{bal.toFixed(3)} SOL{live ? "" : ` · ${rem.toFixed(0)}%`}</span>
      <button className="icon-btn xs danger" title="Remove" onClick={onRemove}><Trash2 size={12} /></button>
    </div>
  );
}

function shortId(id: string): string {
  const t = id.replace(/[^a-zA-Z0-9]/g, "");
  return t.length > 8 ? `${t.slice(0, 4)}…${t.slice(-4)}` : t;
}
